import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LOCALES } from '@gg/shared';
import { siteRules, type SiteRulesContent } from '@gg/shared/i18n';
import { Container, Cta, H1, Section } from '@/components/site/primitives';
import { useSiteContent } from '@/components/site/siteHelpers';
import { DownloadIcon, InfoIcon } from '@/components/site/siteIcons';
import { setLocale } from '@/lib/i18n';

/** /reglement: the full sporting rules, table of contents + articles (#art-1 … #art-19). */
export function Rules() {
  const t: SiteRulesContent = useSiteContent(siteRules);
  const { i18n } = useTranslation();
  const [params, setParams] = useSearchParams();

  // "Lire en ligne" from /conditions opens a given language version (?lang=en), then drops
  // the parameter so the FR/EN switch keeps working.
  const lang = params.get('lang');
  useEffect(() => {
    if (!lang) return;
    if (lang !== i18n.language && (SUPPORTED_LOCALES as readonly string[]).includes(lang)) {
      setLocale(lang);
    }
    setParams({}, { replace: true, preventScrollReset: true });
  }, [lang, i18n.language, setParams]);

  useEffect(() => {
    document.title = t.docTitle;
  }, [t.docTitle]);

  return (
    <>
      <Section tone="dark" topo className="md:pt-20 md:pb-16">
        <Container className="max-w-[1100px]">
          <p className="m-0 mb-4.5">
            <Link to="/conditions" className="text-sm text-site-cream/80 hover:text-site-cream">
              ← {t.back}
            </Link>
          </p>
          <H1 className="text-[40px] md:text-[60px]">{t.title}</H1>
          <p className="m-0 mt-4 font-site-display text-base tracking-[0.16em] text-site-clay-light uppercase">
            {t.sub}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Cta href={t.pdf} download={t.pdfFile} size="md" className="min-h-12">
              <DownloadIcon size={16} />
              {t.download}
            </Cta>
            <button
              type="button"
              lang={t.otherCode}
              onClick={() => setLocale(t.otherCode)}
              className="inline-flex min-h-12 items-center border border-site-cream/40 px-5.5 font-site-display text-sm tracking-[0.14em] text-site-cream uppercase hover:border-site-cream"
            >
              {t.other}
            </button>
          </div>
        </Container>
      </Section>

      <Section className="md:pt-16 md:pb-26">
        <Container className="max-w-[1100px]">
          <p className="m-0 mb-10 flex items-start gap-3 border border-site-clay-light/60 bg-site-clay-light/15 px-5 py-4.5 text-sm leading-normal text-site-ink-body">
            <InfoIcon size={18} className="mt-px flex-none text-site-clay-deep" />
            <span>{t.draft}</span>
          </p>

          <div className="flex flex-wrap items-start gap-12">
            <nav
              aria-label={t.tocLabel}
              className="min-w-0 flex-[1_1_240px] border border-site-line bg-site-card p-6 lg:sticky lg:top-[96px]"
            >
              <p className="m-0 mb-3.5 font-site-display text-[13px] tracking-[0.2em] text-site-olive uppercase">
                {t.toc}
              </p>
              <ol className="m-0 flex list-none flex-col gap-2 p-0 text-sm leading-snug">
                {t.articles.map((a, i) => (
                  <li key={a.title}>
                    <a
                      href={`#art-${i + 1}`}
                      className="inline-block py-0.5 text-site-ink hover:text-site-clay-deep"
                    >
                      <span className="mr-1.5 font-semibold text-site-clay-deep">{i + 1}.</span>
                      {a.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            <div className="flex min-w-0 flex-[999_1_560px] flex-col">
              {t.articles.map((a, i) => (
                <article
                  key={a.title}
                  id={`art-${i + 1}`}
                  className="scroll-mt-[96px] border-t border-site-line py-7"
                >
                  <h2 className="m-0 flex items-baseline gap-3.5 font-site-display text-[22px] leading-tight font-bold uppercase md:text-[26px]">
                    <span className="text-site-clay">{i + 1}</span>
                    <span>{a.title}</span>
                  </h2>
                  {a.blocks.map((b, j) =>
                    b.kind === 'sub' ? (
                      <h3
                        key={j}
                        className="m-0 mt-6 font-site-display text-[17px] font-bold tracking-[0.08em] text-site-olive uppercase"
                      >
                        {b.text}
                      </h3>
                    ) : b.kind === 'warn' ? (
                      <p
                        key={j}
                        className="m-0 mt-4.5 max-w-[720px] border-l-3 border-site-clay bg-site-clay-light/15 px-4 py-3.5 text-sm leading-relaxed text-site-ink-body"
                      >
                        {b.text}
                      </p>
                    ) : (
                      <p
                        key={j}
                        className="m-0 mt-3 max-w-[720px] text-base leading-[1.7] text-site-ink-body"
                      >
                        {b.text}
                      </p>
                    ),
                  )}
                </article>
              ))}
              <div className="flex flex-wrap gap-x-7 gap-y-3 border-t border-site-line pt-7 text-sm">
                <Link to="/conditions" className="text-site-clay-deep hover:text-site-ink">
                  {t.back}
                </Link>
                <a
                  href={t.pdf}
                  download={t.pdfFile}
                  className="text-site-clay-deep hover:text-site-ink"
                >
                  {t.download}
                </a>
              </div>
            </div>
          </div>
        </Container>
      </Section>
    </>
  );
}
